import { clientWithAuth } from 'dashboard409a/services/base';
import { hasStoredAccessToken } from 'pages/Auth/utils';

export const DROPBOX_OAUTH_STATE_KEY = 'dropbox_oauth_state';
export const DROPBOX_OAUTH_RETURN_TO_KEY = 'dropbox_oauth_return_to';
export const DROPBOX_OAUTH_REDIRECT_URI_KEY = 'dropbox_oauth_redirect_uri';
const DROPBOX_OAUTH_HANDLED_CODE_KEY = 'dropbox_oauth_handled_code';

const APP_ACCESS_TOKEN_WAIT_MS = 10_000;
const APP_ACCESS_TOKEN_POLL_MS = 100;

let callbackInFlight: Promise<boolean> | null = null;

export const getDropboxRedirectUri = (): string => {
  const currentUrl = new URL(globalThis.window.location.href);
  return `${currentUrl.origin}${currentUrl.pathname}`;
};

export const removeOAuthParamsFromUrl = (url: URL): URL => {
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('error');
  return url;
};

const clearOAuthStorage = (): void => {
  localStorage.removeItem(DROPBOX_OAUTH_STATE_KEY);
  localStorage.removeItem(DROPBOX_OAUTH_RETURN_TO_KEY);
  localStorage.removeItem(DROPBOX_OAUTH_REDIRECT_URI_KEY);
};

export interface DropboxOAuthAppUrl {
  pathname: string;
  search: string;
  hash: string;
}

const toAppUrl = (url: URL): DropboxOAuthAppUrl => ({
  pathname: url.pathname,
  search: url.search,
  hash: url.hash,
});

const applyAppUrl = (url: DropboxOAuthAppUrl, restoreAppUrl?: (url: DropboxOAuthAppUrl) => void): void => {
  if (restoreAppUrl) {
    restoreAppUrl(url);
    return;
  }
  globalThis.window.history.replaceState(
    {},
    document.title,
    `${url.pathname}${url.search}${url.hash}`
  );
};

const replaceUrlWithoutOAuthParams = (url: URL, restoreAppUrl?: (url: DropboxOAuthAppUrl) => void): void => {
  applyAppUrl(toAppUrl(removeOAuthParamsFromUrl(url)), restoreAppUrl);
};

const buildRestoredAppUrl = (currentUrl: URL): DropboxOAuthAppUrl => {
  const returnTo = localStorage.getItem(DROPBOX_OAUTH_RETURN_TO_KEY);
  const nextUrl = returnTo
    ? removeOAuthParamsFromUrl(new URL(returnTo, currentUrl.origin))
    : removeOAuthParamsFromUrl(new URL(globalThis.window.location.href));
  return toAppUrl(nextUrl);
};

const wasAuthorizationCodeHandled = (code: string): boolean =>
  sessionStorage.getItem(DROPBOX_OAUTH_HANDLED_CODE_KEY) === code;

const markAuthorizationCodeHandled = (code: string): void => {
  sessionStorage.setItem(DROPBOX_OAUTH_HANDLED_CODE_KEY, code);
};

const waitForAppAccessToken = async (): Promise<void> => {
  const deadline = Date.now() + APP_ACCESS_TOKEN_WAIT_MS;
  while (Date.now() < deadline) {
    if (hasStoredAccessToken()) {
      return;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise<void>(resolve => {
      setTimeout(resolve, APP_ACCESS_TOKEN_POLL_MS);
    });
  }
  if (!hasStoredAccessToken()) {
    throw new Error('App session is required before connecting Dropbox');
  }
};

const resolveTokenExchangeRedirectUri = (currentUrl: URL): string => {
  const storedRedirectUri = localStorage.getItem(DROPBOX_OAUTH_REDIRECT_URI_KEY);
  if (storedRedirectUri?.trim()) {
    return storedRedirectUri.trim();
  }
  return `${currentUrl.origin}${currentUrl.pathname}`;
};

export interface ProcessDropboxOAuthCallbackOptions {
  onConnected?: () => void;
  /** Use React Router navigate so query params (e.g. model-id) restore modal state. */
  restoreAppUrl?: (url: DropboxOAuthAppUrl) => void;
}

/**
 * Completes the Dropbox OAuth redirect when `code` is present in the URL.
 * Uses sessionStorage so React Strict Mode remounts cannot exchange the same code twice.
 */
export const processDropboxOAuthCallback = async (
  options: ProcessDropboxOAuthCallbackOptions = {}
): Promise<boolean> => {
  const inFlight = callbackInFlight;
  if (inFlight !== null) {
    return inFlight;
  }

  const run = async (): Promise<boolean> => {
    const currentUrl = new URL(globalThis.window.location.href);
    const code = currentUrl.searchParams.get('code');
    const state = currentUrl.searchParams.get('state');
    const oauthError = currentUrl.searchParams.get('error');
    const expectedState = localStorage.getItem(DROPBOX_OAUTH_STATE_KEY);

    const { restoreAppUrl } = options;

    if (oauthError) {
      replaceUrlWithoutOAuthParams(new URL(globalThis.window.location.href), restoreAppUrl);
      clearOAuthStorage();
      throw new Error(`Dropbox OAuth failed: ${oauthError}`);
    }

    if (!code) {
      return false;
    }

    if (wasAuthorizationCodeHandled(code)) {
      applyAppUrl(buildRestoredAppUrl(currentUrl), restoreAppUrl);
      clearOAuthStorage();
      return true;
    }

    if (expectedState && expectedState !== state) {
      replaceUrlWithoutOAuthParams(new URL(globalThis.window.location.href), restoreAppUrl);
      clearOAuthStorage();
      throw new Error('Dropbox OAuth state mismatch');
    }

    await waitForAppAccessToken();

    const redirectUri = resolveTokenExchangeRedirectUri(currentUrl);

    try {
      await clientWithAuth.post('/api/dropbox/oauth2/token', {
        grant_type: 'authorization_code',
        code,
        redirect_uri: redirectUri,
      });

      markAuthorizationCodeHandled(code);

      applyAppUrl(buildRestoredAppUrl(currentUrl), restoreAppUrl);
      clearOAuthStorage();
      options.onConnected?.();
      return true;
    } catch (error) {
      replaceUrlWithoutOAuthParams(new URL(globalThis.window.location.href), restoreAppUrl);
      clearOAuthStorage();
      throw error;
    }
  };

  callbackInFlight = run().finally(() => {
    callbackInFlight = null;
  });

  return callbackInFlight;
};

export const persistDropboxOAuthStart = (): { redirectUri: string; oauthState: string } => {
  const currentUrl = removeOAuthParamsFromUrl(new URL(globalThis.window.location.href));
  const oauthState = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const redirectUri = getDropboxRedirectUri();

  localStorage.setItem(DROPBOX_OAUTH_STATE_KEY, oauthState);
  localStorage.setItem(DROPBOX_OAUTH_RETURN_TO_KEY, currentUrl.toString());
  localStorage.setItem(DROPBOX_OAUTH_REDIRECT_URI_KEY, redirectUri);

  return { redirectUri, oauthState };
};
